import React, { useEffect, useState } from "react";
import { FileDown, Sparkles, ShieldCheck, KeyRound, Trash2, Copy, Loader2, Calendar, Plus } from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";
import ImagePicker from "./ImagePicker";

const currentYear = new Date().getFullYear();

// ---------------- Annual Report PDF generator ----------------
export const AnnualReportPanel = () => {
    const [year, setYear] = useState(currentYear);
    const [busy, setBusy] = useState(false);

    const generate = async () => {
        setBusy(true);
        try {
            const headers = {};
            const token = localStorage.getItem("rdf_token");
            if (token) headers["Authorization"] = `Bearer ${token}`;
            const resp = await fetch(`${API}/admin/annual-report/${year}`, {
                method: "POST",
                credentials: "include",
                headers,
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
                toast.error(err.detail || "Generate failed");
                return;
            }
            const raised = resp.headers.get("X-Total-Raised");
            const donors = resp.headers.get("X-Donor-Count");
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `reddog-annual-report-${year}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`Annual report ${year}: $${parseFloat(raised || 0).toFixed(2)} raised from ${donors} donors`);
        } catch (err) {
            toast.error(err.message || "Generate failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-black/5 rounded-3xl p-8 mb-8" data-testid="admin-annual-report-section">
            <div className="flex items-start gap-3 mb-6">
                <FileDown className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Annual report</h2>
                    <p className="text-sm text-[#4A5568] mt-1.5 max-w-2xl">
                        One-click board-ready PDF: by-the-numbers strip, impact highlights, every payout listed,
                        and the 501(c)(3) compliance footer. Built directly from your books and public ledger.
                    </p>
                </div>
            </div>

            <div className="flex gap-3 items-end flex-wrap">
                <div>
                    <label htmlFor="annual-year-input" className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-2">Year</label>
                    <input
                        id="annual-year-input"
                        type="number"
                        min={2024}
                        max={currentYear}
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value, 10) || currentYear)}
                        data-testid="admin-annual-report-year-input"
                        className="w-32 rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                    />
                </div>
                <button
                    onClick={generate}
                    disabled={busy}
                    data-testid="admin-annual-report-generate-btn"
                    className="inline-flex items-center justify-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:bg-[#4A5568] disabled:cursor-not-allowed rounded-full px-6 py-3 font-medium transition-all"
                >
                    {busy ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                            Generating…
                        </>
                    ) : (
                        <>
                            <FileDown className="w-4 h-4" strokeWidth={1.5} />
                            Generate & download {year}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

// ---------------- Auditor Tokens panel ----------------
export const AuditorTokensPanel = () => {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ cpa_name: "", cpa_email: "", notes: "", expires_in_days: 14 });
    const [newToken, setNewToken] = useState(null); // surface the raw token ONCE

    const load = () =>
        api.get("/admin/auditor-tokens")
            .then((r) => setTokens(r.data || []))
            .catch(() => {})
            .finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post("/admin/auditor-tokens", form);
            setNewToken(data);
            setForm({ cpa_name: "", cpa_email: "", notes: "", expires_in_days: 14 });
            toast.success("Auditor token created. Copy it now — it won't be shown again.");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (tid) => {
        if (!window.confirm("Revoke this auditor token? The CPA will lose access immediately.")) return;
        try {
            await api.delete(`/admin/auditor-tokens/${tid}`);
            toast.success("Revoked");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Revoke failed");
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
    };

    return (
        <div className="bg-white border border-black/5 rounded-3xl p-8" data-testid="admin-auditor-tokens-section">
            <div className="flex items-start gap-3 mb-6">
                <ShieldCheck className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Auditor tokens</h2>
                    <p className="text-sm text-[#4A5568] mt-1.5 max-w-2xl">
                        Create a time-limited read-only token for an external CPA. They can pull P&amp;L, balance sheet, trial balance,
                        general ledger, and functional expense without needing a user account. Tokens are shown <strong>once</strong> at creation —
                        copy it immediately.
                    </p>
                </div>
            </div>

            {/* New token reveal */}
            {newToken && (
                <div className="bg-[#FDFBF7] border-2 border-[#E53935]/30 rounded-2xl p-5 mb-6" data-testid="admin-auditor-new-token-reveal">
                    <p className="text-xs uppercase tracking-wider font-bold text-[#E53935] mb-2">⚠ Save this token now</p>
                    <p className="text-sm text-[#4A5568] mb-3">
                        Share with <strong>{newToken.cpa_name}</strong> ({newToken.cpa_email}) over a secure channel. Expires {new Date(newToken.expires_at).toLocaleDateString()}.
                    </p>
                    <div className="flex items-center gap-2 bg-white border border-black/10 rounded-xl px-4 py-3">
                        <code className="text-xs flex-1 break-all font-mono text-[#1C252E]" data-testid="admin-auditor-new-token-value">{newToken.token}</code>
                        <button
                            onClick={() => copyToClipboard(newToken.token)}
                            data-testid="admin-auditor-copy-token-btn"
                            className="flex-shrink-0 p-2 hover:bg-[#F4F1EA] rounded-full"
                            aria-label="Copy token"
                            title="Copy to clipboard"
                        >
                            <Copy className="w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                        </button>
                    </div>
                    <p className="text-xs text-[#4A5568] mt-3 font-mono break-all">
                        Example: <strong>curl -H "X-Auditor-Token: {newToken.token.slice(0, 12)}…" {API}/auditor/reports/profit-loss</strong>
                    </p>
                    <button
                        onClick={() => setNewToken(null)}
                        data-testid="admin-auditor-dismiss-token-btn"
                        className="text-xs text-[#4A5568] hover:text-[#E53935] mt-3"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Create form */}
            <form onSubmit={create} className="bg-[#FDFBF7] rounded-2xl p-5 mb-6" data-testid="admin-auditor-create-form">
                <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-4">Create new token</p>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <input
                        required
                        placeholder="CPA name *"
                        value={form.cpa_name}
                        onChange={(e) => setForm({ ...form, cpa_name: e.target.value })}
                        data-testid="admin-auditor-name-input"
                        aria-label="CPA name"
                        className="rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                    />
                    <input
                        required
                        type="email"
                        placeholder="CPA email *"
                        value={form.cpa_email}
                        onChange={(e) => setForm({ ...form, cpa_email: e.target.value })}
                        data-testid="admin-auditor-email-input"
                        aria-label="CPA email"
                        className="rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                    />
                </div>
                <textarea
                    placeholder="Notes (e.g. Q4 2025 audit, Hawkins & Sons CPA)"
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="admin-auditor-notes-input"
                    aria-label="Notes"
                    className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 resize-none mb-3"
                />
                <div className="flex items-center gap-3 flex-wrap">
                    <label htmlFor="auditor-days-input" className="text-sm text-[#4A5568] inline-flex items-center gap-2">
                        <Calendar className="w-4 h-4" strokeWidth={1.5} />
                        Expires in
                    </label>
                    <input
                        id="auditor-days-input"
                        type="number"
                        min={1}
                        max={180}
                        value={form.expires_in_days}
                        onChange={(e) => setForm({ ...form, expires_in_days: parseInt(e.target.value, 10) || 14 })}
                        data-testid="admin-auditor-days-input"
                        className="w-20 rounded-xl border border-black/10 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                    />
                    <span className="text-sm text-[#4A5568]">days</span>
                    <button
                        type="submit"
                        disabled={creating}
                        data-testid="admin-auditor-create-btn"
                        className="ml-auto inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-60 rounded-full px-5 py-2.5 text-sm font-medium transition-all"
                    >
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Plus className="w-4 h-4" strokeWidth={2} />}
                        Create token
                    </button>
                </div>
            </form>

            {/* Tokens list */}
            {loading ? (
                <p className="text-[#4A5568] text-sm">Loading tokens…</p>
            ) : tokens.length === 0 ? (
                <p className="text-[#4A5568] text-sm" data-testid="admin-auditor-empty">No auditor tokens yet.</p>
            ) : (
                <div className="overflow-x-auto" data-testid="admin-auditor-list">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-black/10 text-left text-xs uppercase tracking-wider text-[#4A5568]">
                                <th className="py-3 pr-4">CPA</th>
                                <th className="py-3 pr-4">Created</th>
                                <th className="py-3 pr-4">Expires</th>
                                <th className="py-3 pr-4">Uses</th>
                                <th className="py-3 pr-4">Status</th>
                                <th className="py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tokens.map((t) => {
                                const status =
                                    t.revoked ? { label: "Revoked", color: "bg-gray-100 text-gray-600" } :
                                    t.expired ? { label: "Expired", color: "bg-amber-100 text-amber-700" } :
                                    { label: "Active", color: "bg-green-100 text-green-700" };
                                return (
                                    <tr key={t.id} className="border-b border-black/5" data-testid={`admin-auditor-row-${t.id}`}>
                                        <td className="py-3 pr-4">
                                            <p className="font-medium text-[#1C252E]">{t.cpa_name}</p>
                                            <p className="text-xs text-[#4A5568]">{t.cpa_email}</p>
                                        </td>
                                        <td className="py-3 pr-4 text-[#4A5568] text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                                        <td className="py-3 pr-4 text-[#4A5568] text-xs">{new Date(t.expires_at).toLocaleDateString()}</td>
                                        <td className="py-3 pr-4 text-[#4A5568] text-xs">
                                            {t.use_count || 0}
                                            {t.last_used_at && <span className="block text-[10px]">last: {new Date(t.last_used_at).toLocaleDateString()}</span>}
                                        </td>
                                        <td className="py-3 pr-4">
                                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${status.color}`}>
                                                {status.label}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            {!t.revoked && !t.expired && (
                                                <button
                                                    onClick={() => revoke(t.id)}
                                                    data-testid={`admin-auditor-revoke-${t.id}`}
                                                    className="text-[#E53935] hover:text-[#D32F2F] p-1.5"
                                                    aria-label={`Revoke token for ${t.cpa_name}`}
                                                    title="Revoke"
                                                >
                                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ---------------- Impact Story editor ----------------
// Lightweight modal-style inline form to attach a story to a paid spend-request.
export const ImpactStoryEditor = ({ spendRequest, onClose, onSaved }) => {
    const [form, setForm] = useState({
        impact_headline: spendRequest.impact_headline || "",
        impact_body: spendRequest.impact_body || "",
        impact_photo_url: spendRequest.impact_photo_url || (spendRequest.after_photos || [])[0] || "",
        impact_published: spendRequest.impact_published || false,
    });
    const [saving, setSaving] = useState(false);
    const [notifying, setNotifying] = useState(false);
    const [showPicker, setShowPicker] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/spend-requests/${spendRequest.id}/impact`, form);
            toast.success("Impact story saved");
            if (onSaved) onSaved();
            if (onClose) onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const notify = async () => {
        if (!form.impact_published) {
            toast.error("Save & publish the story first, then notify donors.");
            return;
        }
        if (!window.confirm("Send a 'you helped fund this!' email to every donor whose first gift predates this project's paid date? (Idempotent — donors only get one email per story.)")) return;
        setNotifying(true);
        try {
            const { data } = await api.post(`/admin/impact-notify/${spendRequest.id}`);
            toast.success(`Queued ${data.queued} emails (${data.already_sent} previously notified)`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Notify failed");
        } finally {
            setNotifying(false);
        }
    };

    return (
        <div className="bg-[#FDFBF7] border border-[#E53935]/20 rounded-2xl p-5 mt-3" data-testid={`impact-editor-${spendRequest.id}`}>
            <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
                <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">Impact story</p>
            </div>
            <div className="space-y-3">
                <input
                    placeholder="Headline (e.g. 'New playground at Frenchtown Elementary')"
                    value={form.impact_headline}
                    onChange={(e) => setForm({ ...form, impact_headline: e.target.value })}
                    data-testid={`impact-editor-headline-${spendRequest.id}`}
                    aria-label="Impact headline"
                    maxLength={140}
                    className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                />
                <textarea
                    placeholder="Story body — what happened, who benefited, what comes next"
                    rows={4}
                    value={form.impact_body}
                    onChange={(e) => setForm({ ...form, impact_body: e.target.value })}
                    data-testid={`impact-editor-body-${spendRequest.id}`}
                    aria-label="Impact body"
                    maxLength={3000}
                    className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 resize-none"
                />
                <input
                    placeholder="Hero photo URL (optional — defaults to first after-photo)"
                    value={form.impact_photo_url}
                    onChange={(e) => setForm({ ...form, impact_photo_url: e.target.value })}
                    data-testid={`impact-editor-photo-${spendRequest.id}`}
                    aria-label="Hero photo URL"
                    className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                />
                <button
                    type="button"
                    onClick={() => setShowPicker(true)}
                    data-testid={`impact-editor-pick-${spendRequest.id}`}
                    className="self-start text-xs font-medium text-[#E53935] hover:underline"
                >
                    Pick from library →
                </button>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={form.impact_published}
                        onChange={(e) => setForm({ ...form, impact_published: e.target.checked })}
                        data-testid={`impact-editor-publish-${spendRequest.id}`}
                        className="w-4 h-4 accent-[#E53935]"
                    />
                    <span className="text-sm text-[#1C252E]">Publish to /impact (make it public)</span>
                </label>
                <div className="flex gap-2 pt-2">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid={`impact-editor-save-${spendRequest.id}`}
                        className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-60 rounded-full px-5 py-2 text-sm font-medium"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <KeyRound className="w-4 h-4" strokeWidth={1.5} />}
                        Save story
                    </button>
                    {spendRequest.impact_published && (
                        <button
                            type="button"
                            onClick={notify}
                            disabled={notifying}
                            data-testid={`impact-editor-notify-${spendRequest.id}`}
                            className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black disabled:opacity-60 rounded-full px-5 py-2 text-sm font-medium"
                            title="Email every donor whose first gift predates this project's paid date"
                        >
                            {notifying ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : null}
                            Notify donors
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        data-testid={`impact-editor-cancel-${spendRequest.id}`}
                        className="text-sm text-[#4A5568] hover:text-[#1C252E] px-3"
                    >
                        Cancel
                    </button>
                </div>
            </div>
            <ImagePicker
                open={showPicker}
                onClose={() => setShowPicker(false)}
                onPick={(img) => setForm((prev) => ({ ...prev, impact_photo_url: img.url }))}
                category="impact"
            />
        </div>
    );
};

// Combined wrapper for the new admin tab
const Phase3Panel = () => (
    <div data-testid="admin-phase3-panel">
        <AnnualReportPanel />
        <AuditorTokensPanel />
    </div>
);

export default Phase3Panel;
